import axios from 'axios';

import { type PaginatedListDto, paginatedListDtoSchema } from '@apple/utils/pagination/models';
import type { Guid } from '@apple/utils/primitive';

import {
	type StoreEventCategory,
	type StoreEventCategoryListingRequest,
	type StoreEventCategorySelection,
	type StoreEventCategorySelectionUpdateRequest,
	storeEventCategoryListingRequestSchema,
	storeEventCategorySchema,
	storeEventCategorySelectionSchema,
	storeEventCategorySelectionUpdateRequestSchema,
} from '../models/categories';
import {
	type StoreEventItemConflictGroupModel,
	storeEventItemConflictGroupModelSchema,
} from '../models/conflicts';

/**
 * @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategoryApiController
 * */

/** Gets all of the store event categories. */
export async function getAllCategories(signal: AbortSignal): Promise<StoreEventCategory[]> {
	const response = await getCategories(signal);
	return response.items;
}

/** Gets a paginated list of store event categories based on the supplied filters.*/
export async function getCategories(
	signal: AbortSignal,
	request: Partial<StoreEventCategoryListingRequest> = {},
): Promise<PaginatedListDto<StoreEventCategory>> {
	const params = storeEventCategoryListingRequestSchema.partial().parse(request);
	const response = await axios.get('/api/store-event-catagories', {
		params,
		signal,
	});

	return paginatedListDtoSchema(storeEventCategorySchema).parse(response.data);
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategorySelectionApiController.GetSelections */
export async function getStoreEventCategorySelections(
	storeEventId: Guid,
	signal?: AbortSignal,
): Promise<StoreEventCategorySelection[]> {
	const response = await axios.get(`/api/store-events/${storeEventId}/categories`, {
		signal,
	});

	return storeEventCategorySelectionSchema.array().parse(response.data);
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategorySelectionApiController.Post */
export async function setStoreEventCategorySelections(
	storeEventId: Guid,
	model: StoreEventCategorySelectionUpdateRequest,
): Promise<StoreEventItemConflictGroupModel[] | undefined> {
	model = storeEventCategorySelectionUpdateRequestSchema.parse(model);
	const response = await axios.post(`/api/store-events/${storeEventId}/categories`, model, {
		validateStatus: status => status < 300 || status === 409,
	});

	if (response.status === 409) {
		return storeEventItemConflictGroupModelSchema.array().parse(response.data);
	}
}
